import { InputAdornment, TextField } from '@material-ui/core';
import { useStyles } from './styles';
import ChatSendIcon from '../../assets/svg/chat_send.svg';
// import UploadIcon from '../../assets/svg/add_picture.svg';
import { IMessages } from '.';
import styles from './styles.module.scss';
import { LanguageCode, MessageTypeEnum } from '__generated__/globalTypes';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useReceivedAssistantMessage from './hooks/useReceivedAssistantMessage';
import AmiraFilter from './AmiraFilter';
import { ReactComponent as IconAdd } from '../../assets/svg/icon_add.svg';
import { ReactComponent as IconCamera } from '../../assets/svg/camera.svg';
import { ReactComponent as IconGallery } from '../../assets/svg/photo_gallery.svg';
const INVALID_MESSAGE_CONTENT = "Image couldn't be processed";
type IAmiraFooter = {
    handleTextChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    answerLoading: boolean;
    message: string;
    messages: IMessages[];
    pendingMessages: IMessages[];
    setPendingMessages: (value: []) => void;
    sendMessage: (value: Record<string, unknown>) => void;
    storeSupportedLanguages: LanguageCode[];
    conversationLanguage?: LanguageCode | null;
    focusInput: boolean;
    setFocusInput: (value: boolean) => void;
    isCollectingFeedback?: boolean;
    isSuggestionMessageDisplay: () => boolean;
    isUploadingImage: boolean;
    onImageSelection: (file: File) => Promise<void>;
    isOpenningConverastion: boolean;
};

export const AmiraFooter = (props: IAmiraFooter) => {
    const {
        handleTextChange,
        answerLoading,
        message,
        messages,
        pendingMessages,
        setPendingMessages,
        sendMessage,
        storeSupportedLanguages,
        conversationLanguage,
        focusInput,
        setFocusInput,
        isCollectingFeedback,
        isSuggestionMessageDisplay,
        isUploadingImage,
        onImageSelection,
        isOpenningConverastion,
    } = props;
    const { isReceivedAssistantMessage, setIsReceivedAssistantMessage } = useReceivedAssistantMessage();
    const classes = useStyles();
    const showBlink = useMemo(
        () => !storeSupportedLanguages?.length || storeSupportedLanguages?.length === 2,
        [storeSupportedLanguages],
    );
    const inputRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const inputCameraRef = useRef<HTMLInputElement>(null);
    const [showAttachmentOptions, setShowAttachmentOptions] = useState(false);

    const resetFileInput = useCallback(() => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);
    useEffect(() => {
        const handleBlur = () => {
            if (!message.length && !isCollectingFeedback) {
                setFocusInput(false);
            }
        };
        if (inputRef.current) {
            if (focusInput) inputRef.current.focus();
            inputRef.current.addEventListener('blur', handleBlur);
        }
        return () => {
            if (inputRef.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                inputRef.current.removeEventListener('blur', handleBlur);
            }
        };
    }, [focusInput, setFocusInput, message, isCollectingFeedback]);
    const handleSendMessage = () => {
        if (!answerLoading && message?.trim() && !isReceivedAssistantMessage) {
            if (pendingMessages.length) {
                setPendingMessages([]);
            }
            if (isReceivedAssistantMessage) {
                setIsReceivedAssistantMessage(false);
            }
            setFocusInput(false);
            const isIncludeRequestParameter = !focusInput;
            sendMessage({ isIncludeRequestParameter });
        } else if (isCollectingFeedback && !message?.trim()?.length) {
            setFocusInput(false);
            sendMessage({});
        }
    };

    const handleUploadButtonClick = useCallback(() => {
        if (answerLoading || isUploadingImage) return;
        fileInputRef.current?.click();
    }, [answerLoading, isUploadingImage]);

    const isMobileDevice = useMemo(() => /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent), []);

    const handleCameraClick = useCallback(async () => {
        // Prefer native camera intent on mobile (file input with capture),
        // otherwise try getUserMedia on desktop and fall back to file input if unavailable.
        if (answerLoading || isUploadingImage) return;

        if (!isMobileDevice && navigator.mediaDevices?.getUserMedia) {
            let stream: MediaStream | undefined;
            let video: HTMLVideoElement | undefined;
            try {
                stream = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: { ideal: 'environment' } },
                    audio: false,
                });

                video = document.createElement('video');
                video.autoplay = true;
                video.muted = true;
                video.playsInline = true;
                video.srcObject = stream;

                await new Promise<void>((resolve) => {
                    if (!video) return resolve();
                    video.onloadedmetadata = () => {
                        video?.play().finally(() => resolve());
                    };
                });

                const width = video.videoWidth || 1280;
                const height = video.videoHeight || 720;
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) throw new Error('Canvas 2D context not available');
                ctx.drawImage(video, 0, 0, width, height);

                const blob: Blob = await new Promise((resolve, reject) => {
                    canvas.toBlob(
                        (b) => (b ? resolve(b) : reject(new Error('Failed to capture image'))),
                        'image/jpeg',
                        0.92,
                    );
                });

                const file = new File([blob], 'camera.jpg', { type: 'image/jpeg' });
                await onImageSelection(file);
                return; // success, no need to fall back
            } catch (e) {
                // Fall back to file input method below
            } finally {
                try {
                    if (video) {
                        video.srcObject = null;
                        // Detach any event handlers
                        video.onloadedmetadata = null;
                    }
                    if (stream) {
                        stream.getTracks().forEach((t) => t.stop());
                    }
                } catch (_) {
                    // ignore cleanup errors
                }
            }
        }

        // Mobile or unsupported: use the hidden file input with capture
        if (inputCameraRef.current) {
            inputCameraRef.current.click();
        }
    }, [answerLoading, isMobileDevice, isUploadingImage, onImageSelection]);

    const handleFileInputChange = useCallback(
        async (event: ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) {
                resetFileInput();
                return;
            }
            try {
                await onImageSelection(file);
            } finally {
                resetFileInput();
            }
        },
        [onImageSelection, resetFileInput],
    );

    const inputClasses = useMemo(() => {
        const focusClass = focusInput ? (isCollectingFeedback ? 'feedback-reason' : 'other-option') : null;
        let languageClass;

        if (conversationLanguage) {
            languageClass = conversationLanguage;
        } else if (showBlink) {
            languageClass = 'both';
        } else {
            languageClass = storeSupportedLanguages[0];
        }

        if (focusClass) {
            return styles[`amiraInput__supported-language-${focusClass}-${languageClass}`];
        }

        return styles[`amiraInput__supported-language-${languageClass}`];
    }, [conversationLanguage, focusInput, isCollectingFeedback, showBlink, storeSupportedLanguages]);
    const submitedUserMesage = useMemo(() => {
        let messageSize = messages.length;
        const totalInvalidMessages = messages.filter(
            (msg) => msg.type === MessageTypeEnum.user_uploaded_image && msg.content === INVALID_MESSAGE_CONTENT,
        ).length;
        messageSize -= totalInvalidMessages * 2;
        if (isOpenningConverastion) {
            messageSize -= 5;
        } else {
            messageSize -= 1;
        }
        return messageSize > 0;
    }, [isOpenningConverastion, messages]);
    return (
        <div
            className={classes.footerContainer}
            style={{ display: isReceivedAssistantMessage || answerLoading ? 'none' : '' }}
        >
            <div className={classes.footerShadowContainer}>
                {isSuggestionMessageDisplay() ? <AmiraFilter /> : null}
                <div className={`${styles.amiraInput} ${inputClasses} ${classes.customTextField} text-field-container`}>
                    <TextField
                        className={classes.textField}
                        multiline
                        maxRows={4}
                        placeholder=" "
                        onChange={handleTextChange}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                                handleSendMessage();
                                e.preventDefault(); // prevent adding a newline
                            }
                        }}
                        disabled={answerLoading}
                        value={message}
                        style={{ display: isReceivedAssistantMessage || answerLoading ? 'none' : '' }}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment
                                    position="end"
                                    onClick={() => handleSendMessage()}
                                    style={{ cursor: answerLoading ? 'not-allowed' : 'pointer' }}
                                >
                                    <img src={ChatSendIcon} alt="Taffi" height={30} width={30} />
                                </InputAdornment>
                            ),
                            inputRef,
                        }}
                    />
                    <div
                        className="icons-container ml-8"
                        style={{ display: showAttachmentOptions && !submitedUserMesage && !message ? 'flex' : 'none' }}
                    >
                        <IconCamera
                            style={{ cursor: 'pointer', height: '24px', width: '24px' }}
                            onClick={() => {
                                if (answerLoading || isUploadingImage) {
                                    return;
                                }
                                handleCameraClick();
                            }}
                        />
                        <IconGallery
                            className="ml-8"
                            style={{ cursor: 'pointer', height: '24px', width: '24px' }}
                            onClick={() => {
                                if (answerLoading || isUploadingImage) {
                                    return;
                                }
                                handleUploadButtonClick();
                            }}
                        />
                        <input
                            type="file"
                            hidden
                            accept="image/*"
                            capture="environment"
                            ref={inputCameraRef}
                            onChange={handleFileInputChange}
                        />
                    </div>
                    <div
                        className="add-icon ml-8"
                        onClick={() => setShowAttachmentOptions(true)}
                        style={{ display: !showAttachmentOptions && !submitedUserMesage && !message ? 'flex' : 'none' }}
                    >
                        <IconAdd style={{ cursor: 'pointer', height: '20px', width: '20px' }} />
                    </div>
                </div>
            </div>
            <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileInputChange}
            />
        </div>
    );
};
